import classnames from 'classnames';
import EventInfo from '../../../EventInfo';
import DiscountCode from '../../../DiscountCode';
import { useMultiTierCheckout } from '../../context';
import { StepProps, StepsId } from '../../types';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import TicketTierCard from './components/TicketTierCard';
import tiersStyles from '../../../Tiers.module.scss';
import styles from '../../../Checkout.module.scss';
import GoToRequestScreeningButton from '../GoToRequestScreeningButton';
import { useMemo, useState } from 'react';
import { EventPricingTier } from 'legacy/common/types/models';
import { IoChevronDown } from 'react-icons/io5';

const TicketsSelectionStep = ({ goToStep }: StepProps) => {
  const {
    data: { event, appliedDiscount, tickets, checkoutSession, ticketTiers }
  } = useMultiTierCheckout();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const goToPrivateCodeForm = () => {
    goToStep(StepsId.PrivateCodeStep);
  };

  const goToNextStep = () => {
    goToStep(StepsId.ConfirmTransactionStep);
  };

  // Filter out private tiers
  const privateTiers = useMemo(
    () => ticketTiers?.data?.filter((tier) => tier.private),
    [ticketTiers?.data]
  );

  // Filter out private tiers and sort by sortIndex
  const sortedTicketTiers = useMemo(
    () =>
      ticketTiers?.data
        ?.filter((tier) => !tier.private)
        .sort((a, b) => a.sortIndex - b.sortIndex),
    [ticketTiers?.data]
  );

  // Sort sections by sortIndex
  const sortedSections = useMemo(
    () => event?.pricing?.sections?.sort((a, b) => a.sortIndex - b.sortIndex),
    [event?.pricing?.sections]
  );

  // Group tickets by section
  const ticketsBySection = useMemo(
    () =>
      sortedTicketTiers?.reduce((acc, tier) => {
        const sectionId = tier.sectionId || 'default';
        acc[sectionId] = [...(acc[sectionId] || []), tier];
        return acc;
      }, {} as Record<string, EventPricingTier[]>),
    [sortedTicketTiers]
  );

  // Hide sections that don't have any tickets
  const filteredSections = useMemo(
    () =>
      sortedSections?.filter(
        (section) => ticketsBySection?.[section.id]?.length > 0
      ),
    [sortedSections, ticketsBySection]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.column}>
        <div className="px-5 pb-16">
          <EventInfo event={event} />

          {checkoutSession?.checkoutError?.message &&
          (checkoutSession.isCheckoutDisabled ||
            Object.keys(tickets.selectedTicketTiers).length === 0) ? (
            <div className="text-error text-center py-2">
              {checkoutSession.checkoutError.message}
            </div>
          ) : (
            <div className="flex justify-between items-center mt-6">
              <div className="text-base text-gray-500 font-normal">Tickets</div>
              <div
                className="text-base text-primary-500 cursor-pointer md:text-sm"
                onClick={goToPrivateCodeForm}
              >
                Have a special code?
              </div>
            </div>
          )}

          {appliedDiscount.data && (
            <DiscountCode
              eventId={event._id}
              code={appliedDiscount.data.code}
            />
          )}
          <div className={tiersStyles.tiers}>
            {sortedTicketTiers.length === 0 && privateTiers?.length === 0 ? (
              <div className="mt-10 p-12 text-center text-base text-gray-500 font-normal">
                You need a private code from the host to purchase a ticket to
                this event.
              </div>
            ) : (
              <>
                {/* Private section for private tickets */}
                {privateTiers?.length > 0 && (
                  <div className="mt-10 mb-14">
                    <h3 className="text-xl font-semibold text-gray-500">
                      Private Tickets
                    </h3>
                    <div className="flex flex-col gap-4">
                      {privateTiers.map((ticketTier) => (
                        <TicketTierCard
                          key={ticketTier._id}
                          ticketTier={ticketTier}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Default section for tickets without sectionId */}
                {ticketsBySection?.default?.length > 0 && (
                  <div className="mb-14">
                    {privateTiers?.length > 0 && (
                      <h3 className="mt-10 text-xl font-semibold text-gray-500">
                        Tickets
                      </h3>
                    )}
                    <div className="flex flex-col gap-4">
                      {ticketsBySection?.default?.map((ticketTier) => (
                        <TicketTierCard
                          key={ticketTier._id}
                          ticketTier={ticketTier}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Other sections */}
                {filteredSections?.map((section) => (
                  <div key={section.id} className="mt-4">
                    <div
                      className={classnames(
                        'flex items-center justify-between cursor-pointer bg-gray-100 rounded-md py-4 px-4',
                        {
                          'rounded-b-none': expandedSections[section.id]
                        }
                      )}
                      onClick={() => toggleSection(section.id)}
                    >
                      <h3 className="text-xl font-semibold text-gray-800 text-center ">
                        {section.name}
                      </h3>
                      <IoChevronDown
                        size={24}
                        className={`text-gray-500 transition-transform duration-200 ${
                          expandedSections[section.id]
                            ? 'transform rotate-180'
                            : ''
                        }`}
                      />
                    </div>
                    {!expandedSections[section.id] && (
                      <div className="flex flex-col gap-4 px-4 pb-8 mb-6 border-b border-l border-r border-dashed rounded-b-md">
                        {ticketsBySection?.[section.id]?.map((ticketTier) => (
                          <TicketTierCard
                            key={ticketTier._id}
                            ticketTier={ticketTier}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
      {tickets.tierRequiringApproval ? (
        <div className={classnames(styles.nextButtonWrapperMulti)}>
          <div className="text-sm font-normal text-neutral-500 pb-2">
            No other tiers can be selected after selecting a tier that requires
            approval.
          </div>
          <GoToRequestScreeningButton goToStep={goToStep} />
        </div>
      ) : (
        Object.keys(tickets.selectedTicketTiers).length > 0 && (
          <div className={classnames(styles.nextButtonWrapperMulti)}>
            {checkoutSession.checkoutError?.message && (
              <div className="text-error text-center pb-2">
                {checkoutSession.checkoutError.message}
              </div>
            )}
            <Button
              loading={checkoutSession.isLoadingCheckout}
              onClick={goToNextStep}
              disabled={Boolean(checkoutSession.checkoutError)}
              type={ButtonType.Primary}
            >
              Next
            </Button>
          </div>
        )
      )}
    </div>
  );
};

export default TicketsSelectionStep;
